import { Link } from 'react-router-dom';
import ChecklistOutlinedIcon from '@mui/icons-material/ChecklistOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import HubOutlinedIcon from '@mui/icons-material/HubOutlined';
import LocalLibraryOutlinedIcon from '@mui/icons-material/LocalLibraryOutlined';
import TravelExploreOutlinedIcon from '@mui/icons-material/TravelExploreOutlined';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import NewReleasesOutlinedIcon from '@mui/icons-material/NewReleasesOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { PageHero } from '@/components/PageHero';
import { Reveal } from '@/components/Reveal';
import { useSeo } from '@/seo/useSeo';

type Resource = { icon: SvgIconComponent; title: string; body: string; to: string };

const resources: Resource[] = [
  {
    icon: ChecklistOutlinedIcon,
    title: "Buyer's guide",
    body: 'A vendor-neutral checklist for evaluating an RMM + PSA platform — architecture, data ownership, and your worst day.',
    to: '/buyers-guide',
  },
  {
    icon: FactCheckOutlinedIcon,
    title: 'Compliance frameworks',
    body: 'SOC 2, NIST CSF, HIPAA, PCI-DSS, ISO 27001, and FERPA/COPPA — what an MSP needs to track for each.',
    to: '/compliance',
  },
  {
    icon: HubOutlinedIcon,
    title: 'Integrations',
    body: 'Syncro, Jamf, Intune, NinjaOne, Action1, Google Workspace, BCDR vendors, and network monitoring gear — how existing tools fold into Nexus.',
    to: '/integrations',
  },
  {
    icon: LocalLibraryOutlinedIcon,
    title: 'Glossary',
    body: 'Plain-language definitions for MSP, RMM, PSA, agentic AI, vCIO, and the rest of the industry vocabulary.',
    to: '/glossary',
  },
  {
    icon: TravelExploreOutlinedIcon,
    title: 'Roadmap',
    body: 'Where Nexus actually is today, phase by phase — updated by hand, not on a marketing schedule.',
    to: '/roadmap',
  },
  {
    icon: NewReleasesOutlinedIcon,
    title: "What's new",
    body: 'A dated record of what actually shipped — merged and running, with an honest note on what each change is not yet.',
    to: '/whats-new',
  },
  {
    icon: ArticleOutlinedIcon,
    title: 'Blog',
    body: 'Architecture decisions, security design notes, and honest looks at what MSP tooling should actually do.',
    to: '/blog',
  },
];

export function Resources(): JSX.Element {
  useSeo({
    title: 'Resources',
    description:
      'Everything to research Nexus and MSP tooling in general — the buyer\'s guide, compliance frameworks, integrations, glossary, roadmap, and blog, all in one place.',
    path: '/resources',
  });

  return (
    <div>
      <PageHero
        eyebrow="Resources"
        title="Everything to research this, in one place."
        subtitle="Whether you're evaluating vendors, checking a compliance framework, or just learning the vocabulary — start here."
      />

      <section className="pb-16 md:pb-24">
        <div className="container-content">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {resources.map((r, i) => {
              const Icon = r.icon;
              return (
                <Reveal key={r.to} delay={(i % 3) * 80}>
                  <Link
                    to={r.to}
                    className="group flex h-full flex-col rounded-2xl border border-white/10 bg-white/[0.02] p-6 no-underline transition-all duration-200 hover:-translate-y-1 hover:border-nexus-purple/50"
                  >
                    <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-nexus-purple/[0.14] text-nexus-purple-light transition-transform duration-200 group-hover:scale-110">
                      <Icon fontSize="small" />
                    </div>
                    <h2 className="font-display text-[17px] font-semibold text-nexus-ink">{r.title}</h2>
                    <p className="mt-1.5 flex-1 text-sm text-white/65">{r.body}</p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
